import { apiRequest, apiRequestWithEtag } from '@/domains/api';
import type { IEtagged } from '@/domains/api';
import type {
  ICatalogCycle,
  ICatalogSurvey,
  ICorrection,
  IOversightResponseDetail,
  IOversightResponseRow,
  IPage,
  IRaiseFlag,
  IResponseQuery,
  IReviewFlag,
  IStaffNote,
  ITimelineRow,
  TimelineSource
} from './oversight-contracts';

/**
 * survey-api's staff oversight surface — its seventeen routes under `/api/v1/admin` (responses,
 * their corrections and the recycle bin's two moves, the filter catalogue, timeline, flags and
 * notes), one function each.
 *
 * It is one of several readers of that prefix, split by what they read rather than by where
 * the route happens to be mapped: accounts are `accounts-api.ts`, the person hub, the People
 * list and the registration queue are `participants-api.ts`, and `admin-api.ts` is the one
 * remaining mock — the cross-version diff. Keeping them in separate modules is what makes
 * "which screen is real" answerable by looking at its imports rather than by reading a notice.
 * The participant **notes** stay here, every half: survey-api mapped the note write beside
 * the note list, one set of routes for one resource — the author's edit and delete
 * (`/admin/notes/{id}`, SR-222) included.
 *
 * Two things it deliberately does not do:
 *
 * - **No filter that the API does not have.** Answer-value search, date ranges and
 *   client-chosen sorting are all out of the response list's v1 by name, so there is no
 *   function here that would have to fake one over a single page of rows. The list's `q` is
 *   survey-api's record search, People's engine, answered over the whole list; `participantId`
 *   is kept with no caller, since the hub reads the household's responses in its own document.
 * - **No caching, no store.** Every call is a fetch; the pages hold what they render. The
 *   respondent runtime has a state document to keep, this surface has lists to read.
 *
 * The one write on an answer is a staff **correction** (SR-222): a submitted or locked
 * response's answer changed in place, with a reason and the revision the detail's `ETag` gave.
 * The writes on a status are the recycle bin's (SR-470): a move to the bin and a restore from it,
 * under the same precondition. Each is survey-api's write, judged there — this module only
 * carries it.
 *
 * Errors arrive as `ApiError` from the shared client, carrying survey-api's ProblemDetails
 * — `invalid-query` (400) on a bad filter, `invalid-body` (400) and `invalid-action` (422)
 * on a refused write, `flag-not-open` (409) on a repeated transition, `transition-refused` and
 * `live-slot-taken` (409) on a recycle bin move, `not-note-author` (403) on another's note. `403`
 * is what an advisor gets, from the policy, before any lookup.
 */

const BASE = '/api/v1/admin';

/** Drops empty parameters rather than sending `?surveyCode=`, which is not the same query. */
function queryString(entries: [string, string | number | undefined][]): string {
  const params = new URLSearchParams();
  for (const [key, value] of entries) {
    if (value === undefined || value === '') continue;
    params.append(key, String(value));
  }
  const rendered = params.toString();
  return rendered ? `?${rendered}` : '';
}

/** A recycle bin move: the reason goes out only when there is one, as survey-api's body is
 *  `{ reason? }`. */
function statusChange(
  move: 'recycle' | 'restore',
  responseId: string,
  etag: string,
  reason: string
): Promise<IEtagged<IOversightResponseDetail>> {
  return apiRequestWithEtag<IOversightResponseDetail>(`${BASE}/responses/${responseId}/${move}`, {
    method: 'POST',
    body: reason.trim() === '' ? {} : { reason },
    headers: { 'If-Match': etag }
  });
}

export const oversightApi = {
  /**
   * Responses across every participant — the discovery the respondent surface deliberately
   * lacks: `/my/cycles` scopes its responses to the caller's own participant links even
   * for staff and admin.
   *
   * `status` is repeated once per value; an unknown code answers 400 rather than an empty
   * page, and none means every status but `recycled` — the recycle bin is
   * `status: ['recycled']`. Ordering is fixed at `updatedAt DESC, responseId ASC` and there
   * is no sort parameter to pass.
   */
  listResponses(
    query: IResponseQuery = {},
    signal?: AbortSignal
  ): Promise<IPage<IOversightResponseRow>> {
    const params = new URLSearchParams();
    const scalars: [string, string | number | undefined][] = [
      ['surveyCode', query.surveyCode],
      ['cycleCode', query.cycleCode],
      ['surveyCycleId', query.surveyCycleId],
      ['surveyVersionId', query.surveyVersionId],
      ['participantId', query.participantId],
      ['pathCode', query.pathCode],
      ['q', query.q],
      ['page', query.page],
      ['pageSize', query.pageSize]
    ];
    for (const [key, value] of scalars) {
      if (value === undefined || value === '') continue;
      params.append(key, String(value));
    }
    for (const status of query.status ?? []) params.append('status', status);

    const rendered = params.toString();
    return apiRequest<IPage<IOversightResponseRow>>(
      `${BASE}/responses${rendered ? `?${rendered}` : ''}`,
      { signal }
    );
  },

  /**
   * One response as the whole of its **pinned** questionnaire: sections in order, every
   * usage present whether or not it was answered, answers as stored. No render state and
   * no findings — see `oversight-contracts.ts` on why the engine does not run here.
   *
   * With its `ETag` — the revision, quoted back verbatim as a correction's `If-Match`. A
   * `null` one (an API from before SR-222) is never read as "no precondition": the caller
   * turns corrections off.
   */
  getResponse(
    responseId: string,
    signal?: AbortSignal
  ): Promise<IEtagged<IOversightResponseDetail>> {
    return apiRequestWithEtag<IOversightResponseDetail>(`${BASE}/responses/${responseId}`, {
      signal
    });
  },

  /**
   * A staff correction — 200 with the whole detail and its new `ETag`, which the caller adopts
   * as its read. The precondition is checked before anything else (428 without one), then the
   * body (400), existence (404), the status (409 `response-not-writable`), the revision (409
   * `revision-conflict`) and the writes (422 `writes-rejected`, `constraint` included).
   */
  correct(
    responseId: string,
    etag: string,
    body: ICorrection
  ): Promise<IEtagged<IOversightResponseDetail>> {
    return apiRequestWithEtag<IOversightResponseDetail>(
      `${BASE}/responses/${responseId}/corrections`,
      { method: 'POST', body, headers: { 'If-Match': etag } }
    );
  },

  /**
   * A draft, submitted or locked response moved to the recycle bin (survey-api's recycle bin
   * design §4) — 200 with the detail as it reads after the move and its new `ETag`, which the
   * caller adopts. Checked as a correction is: the precondition (428), the reason (400 over 2 000
   * trimmed), existence (404), then the move — 409 `transition-refused` with the `status` the
   * response is in (recycled or archived), or `revision-conflict`. A blank reason is left out.
   */
  recycle(
    responseId: string,
    etag: string,
    reason: string
  ): Promise<IEtagged<IOversightResponseDetail>> {
    return statusChange('recycle', responseId, etag, reason);
  },

  /**
   * A recycled response restored to the status it left — as `recycle`, with
   * `transition-refused` when it is not in the bin and `live-slot-taken` with the `responseId`
   * holding the participant's live slot in the cycle.
   */
  restore(
    responseId: string,
    etag: string,
    reason: string
  ): Promise<IEtagged<IOversightResponseDetail>> {
    return statusChange('restore', responseId, etag, reason);
  },

  /** The values the list's filters take. Unpaged — four surveys and tens of cycles. */
  surveys(signal?: AbortSignal): Promise<ICatalogSurvey[]> {
    return apiRequest<ICatalogSurvey[]>(`${BASE}/surveys`, { signal });
  },

  cycles(surveyCode?: string, signal?: AbortSignal): Promise<ICatalogCycle[]> {
    return apiRequest<ICatalogCycle[]>(
      `${BASE}/cycles${queryString([['surveyCode', surveyCode]])}`,
      { signal }
    );
  },

  /**
   * `audit_event`, the migrated `legacy_survey_log`, the staff fields' events and Salesforce's
   * field history (`salesforce`, SR-222's block 9), merged and paged. `usage` keeps one
   * question's rows — every instance of it, its Salesforce rows' roster slots among them, and
   * never the `internal` source; an unknown code is 400 `invalid-query`.
   */
  timeline(
    responseId: string,
    query: { source?: TimelineSource; usage?: string; page?: number; pageSize?: number } = {},
    signal?: AbortSignal
  ): Promise<IPage<ITimelineRow>> {
    return apiRequest<IPage<ITimelineRow>>(
      `${BASE}/responses/${responseId}/timeline${queryString([
        ['source', query.source],
        ['usage', query.usage],
        ['page', query.page],
        ['pageSize', query.pageSize]
      ])}`,
      { signal }
    );
  },

  /** Every flag of the response, any status. Unpaged: flags per response are single digits. */
  flags(responseId: string, signal?: AbortSignal): Promise<IReviewFlag[]> {
    return apiRequest<IReviewFlag[]>(`${BASE}/responses/${responseId}/flags`, { signal });
  },

  raiseFlag(responseId: string, body: IRaiseFlag): Promise<IReviewFlag> {
    return apiRequest<IReviewFlag>(`${BASE}/responses/${responseId}/flags`, {
      method: 'POST',
      body
    });
  },

  /**
   * `open` → `resolved` / `dismissed`, and only from `open`: repeating a transition is a
   * 409 `flag-not-open` rather than a success that changed nothing.
   */
  setFlagStatus(
    responseId: string,
    flagId: string,
    status: 'resolved' | 'dismissed'
  ): Promise<IReviewFlag> {
    return apiRequest<IReviewFlag>(`${BASE}/responses/${responseId}/flags/${flagId}`, {
      method: 'PATCH',
      body: { status }
    });
  },

  responseNotes(
    responseId: string,
    query: { page?: number; pageSize?: number } = {},
    signal?: AbortSignal
  ): Promise<IPage<IStaffNote>> {
    return apiRequest<IPage<IStaffNote>>(
      `${BASE}/responses/${responseId}/notes${queryString([
        ['page', query.page],
        ['pageSize', query.pageSize]
      ])}`,
      { signal }
    );
  },

  writeNote(responseId: string, body: string): Promise<IStaffNote> {
    return apiRequest<IStaffNote>(`${BASE}/responses/${responseId}/notes`, {
      method: 'POST',
      body: { body }
    });
  },

  /**
   * A participant's whole note history, including notes on their other responses and notes
   * attached to no response at all. `staff_note` hangs off the participant precisely
   * because a note about a firm outlives any one cycle's response.
   */
  participantNotes(
    participantId: string,
    query: { page?: number; pageSize?: number } = {},
    signal?: AbortSignal
  ): Promise<IPage<IStaffNote>> {
    return apiRequest<IPage<IStaffNote>>(
      `${BASE}/participants/${participantId}/notes${queryString([
        ['page', query.page],
        ['pageSize', query.pageSize]
      ])}`,
      { signal }
    );
  },

  /**
   * A note about the participant rather than about one of their responses — 201 with a note
   * whose `responseId` is null. It lands in `participantNotes` beside the ones written from a
   * response. Blank or over `NOTE_MAX_LENGTH` is a 400; an unknown participant 404.
   */
  writeParticipantNote(participantId: string, body: string): Promise<IStaffNote> {
    return apiRequest<IStaffNote>(`${BASE}/participants/${participantId}/notes`, {
      method: 'POST',
      body: { body }
    });
  },

  /**
   * The author's edit — 200 with the note, its `updatedAt` moved. Anybody else, an admin
   * included, is 403 `not-note-author`; an unknown or deleted note 404; a blank or over-long
   * body 400 at `body`.
   */
  editNote(noteId: string, body: string): Promise<IStaffNote> {
    return apiRequest<IStaffNote>(`${BASE}/notes/${noteId}`, {
      method: 'PATCH',
      body: { body }
    });
  },

  /** The author's delete — 204, soft: every note read leaves it out. Refused as the edit is. */
  async deleteNote(noteId: string): Promise<void> {
    await apiRequest<null>(`${BASE}/notes/${noteId}`, { method: 'DELETE' });
  }
};
